import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "@/components/grid/ag-grid-theme.css";
import type { ColDef } from "ag-grid-community";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import type { ProjectRun } from "@/api/projects";
import {
  ProgressCell,
  RelativeTimeCell,
  RunIdCell,
  RunStateCell,
  WorkloadCell,
} from "@/components/grid/cells";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

/**
 * Tenant- or project-scoped run feed rendered via AG-Grid Quartz on
 * the FlowStack theme. Filters + sort serialize to the URL so a
 * link to a filtered view round-trips through paste/bookmark.
 *
 * Filter state lives in ``?q=&state=`` query params:
 *   ?q=run-abc            quick search across run_id + tile/flow
 *   ?state=running         filter to a specific lifecycle state
 *
 * A single ``RunListAgGrid`` instance powers both the tenant-wide
 * /runs route and the project-scoped /projects/:slug detail page —
 * the only difference is which TanStack hook supplies ``runs``.
 */
export interface RunListAgGridProps {
  runs: ProjectRun[];
  /** When true, hide the Project column (project-scoped page already
   * knows which project these belong to). */
  showProjectColumn?: boolean;
  className?: string;
}

export function RunListAgGrid({
  runs,
  showProjectColumn = true,
  className,
}: RunListAgGridProps): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = searchParams.get("q") ?? "";
  const stateFilter = searchParams.get("state") ?? "";

  const filteredRuns = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    return runs.filter((row) => {
      if (stateFilter && row.status !== stateFilter) return false;
      if (!needle) return true;
      const hay = [
        row.run_id,
        row.tile_name ?? "",
        row.flow_name ?? "",
        row.submitted_by,
        row.project_slug ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [runs, q, stateFilter]);

  const columnDefs = React.useMemo<ColDef<ProjectRun>[]>(() => {
    const cols: ColDef<ProjectRun>[] = [
      {
        field: "run_id",
        headerName: "Run",
        cellRenderer: RunIdCell,
        flex: 1,
        minWidth: 160,
        sortable: true,
      },
      {
        headerName: "Workload",
        valueGetter: (params) => params.data?.flow_name ?? params.data?.tile_name ?? "",
        cellRenderer: WorkloadCell,
        flex: 1.4,
        minWidth: 180,
      },
      {
        field: "status",
        headerName: "State",
        cellRenderer: RunStateCell,
        width: 180,
        sortable: true,
      },
      {
        headerName: "Progress",
        cellRenderer: ProgressCell,
        width: 100,
      },
      {
        field: "submitted_at",
        headerName: "Submitted",
        cellRenderer: RelativeTimeCell,
        width: 140,
        sortable: true,
        sort: "desc",
      },
      {
        field: "submitted_by",
        headerName: "By",
        width: 160,
        sortable: true,
      },
    ];
    if (showProjectColumn) {
      cols.splice(2, 0, {
        field: "project_slug",
        headerName: "Project",
        width: 140,
        sortable: true,
        valueGetter: (params) => params.data?.project_slug ?? "—",
      });
    }
    // Per Rupert PR-259 r1: the placeholder Cost column read as "no
    // cost data for any run" rather than "feature pending." Drop it
    // until the ledger exposes per-run totals — keep CostCell in the
    // renderer set so submission / DAG callers can still use it.
    return cols;
  }, [showProjectColumn]);

  return (
    <div className={className}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          aria-label="Search runs"
          placeholder="Search by id, tile, flow, submitter…"
          value={q}
          onChange={(e) => {
            const next = e.target.value;
            setSearchParams(
              (prev) => {
                const params = new URLSearchParams(prev);
                if (next) params.set("q", next);
                else params.delete("q");
                return params;
              },
              { replace: true }
            );
          }}
          className="max-w-sm"
        />
        <Select
          size="sm"
          aria-label="Filter by state"
          value={stateFilter}
          onChange={(e) => {
            const next = e.target.value;
            setSearchParams(
              (prev) => {
                const params = new URLSearchParams(prev);
                if (next) params.set("state", next);
                else params.delete("state");
                return params;
              },
              { replace: true }
            );
          }}
        >
          {/* Per Rupert PR-259 r1 (blocker): the filter must enumerate
           * every LifecycleStateLabel; otherwise runs in the omitted
           * pre/post-dispatch states are unreachable via the UI even
           * though the canonical state machine produces them. */}
          <option value="">All states</option>
          <option value="validating">validating</option>
          <option value="cached">cached</option>
          <option value="staging_inputs">staging_inputs</option>
          <option value="dispatched">dispatched</option>
          <option value="running">running</option>
          <option value="collecting_outputs">collecting_outputs</option>
          <option value="validating_outputs">validating_outputs</option>
          <option value="completed">completed</option>
          <option value="failed_validation">failed_validation</option>
          <option value="failed_system">failed_system</option>
          <option value="failed_contract">failed_contract</option>
          <option value="failed_scientific">failed_scientific</option>
        </Select>
        <span className="ml-auto text-xs text-muted-foreground">
          {filteredRuns.length} of {runs.length} {runs.length === 1 ? "run" : "runs"}
        </span>
      </div>
      <div className="ag-theme-flowstack h-[560px] overflow-hidden rounded-lg border border-border-subtle">
        <AgGridReact<ProjectRun>
          rowData={filteredRuns}
          columnDefs={columnDefs}
          rowSelection={{ mode: "singleRow" }}
          suppressCellFocus
          suppressMovableColumns
          animateRows={false}
          onRowClicked={(event) => {
            if (event.data) navigate(`/runs/${event.data.run_id}`);
          }}
          getRowId={(params) => params.data.run_id}
        />
      </div>
    </div>
  );
}
